import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CalendarIcon, ArrowRightIcon, RefreshCwIcon, X, Trash2, Split } from 'lucide-react';
import { useTransactionStore } from '@flows.cash/core';
import { useAccountsStore } from '@flows.cash/core';
import { TransferTransaction, UpdateTransferRequest } from '@flows.cash/core';
import { useAuthStore } from '@flows.cash/core';
import { toast } from 'sonner';
import { StyledModal, StyledModalButton } from '@/components/ui/styled-modal';

interface TransferEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  transferId?: string;
  transactionId?: string;
  onSaved?: () => void;
}

export function TransferEditModal({ isOpen, onClose, transferId, transactionId, onSaved }: TransferEditModalProps) {
  const { user } = useAuthStore();
  const { accounts } = useAccountsStore();
  const { updateTransfer, getTransferById, getTransferByTransactionId, deleteTransfer, splitTransfer } = useTransactionStore();
  
  const [transfer, setTransfer] = useState<TransferTransaction | null>(null);
  const [actualTransferId, setActualTransferId] = useState<string>('');
  const [formData, setFormData] = useState({
    amount: '',
    convertedAmount: '',
    exchangeRate: '',
    date: '',
    description: '',
    notes: '',
    tags: [] as string[]
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load transfer data when modal opens
  useEffect(() => {
    if (isOpen && (transferId || transactionId) && user?.id) {
      loadTransferData();
    }
  }, [isOpen, transferId, transactionId, user?.id]);

  const loadTransferData = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      let transferData: TransferTransaction | null = null;
      
      if (transferId) {
        // Direct transfer ID provided
        try {
          transferData = await getTransferById(user.id, transferId);
          setActualTransferId(transferId);
        } catch (error) {
          console.warn('Failed to load transfer by ID:', error);
          transferData = null;
        }
      } else if (transactionId) {
        // Transaction ID provided, need to look up the transfer
        try {
          transferData = await getTransferByTransactionId(user.id, transactionId);
          if (transferData) {
            setActualTransferId(transferData.id);
          }
        } catch (error) {
          console.warn('Failed to load transfer by transaction ID:', error);
          transferData = null;
        }
      }
      
      if (transferData) {
        setTransfer(transferData);
        setFormData({
          amount: transferData.amount.toString(),
          convertedAmount: transferData.convertedAmount.toString(),
          exchangeRate: transferData.exchangeRate.toString(),
          date: transferData.date.slice(0, 16), // Format for datetime-local input (YYYY-MM-DDTHH:MM)
          description: transferData.description || '',
          notes: transferData.notes || '',
          tags: transferData.tags || []
        });
      } else {
        // If we can't load as a transfer, show an error message
        toast.error('Unable to load transfer data', {
          description: 'This may be a legacy transfer that needs to be edited using the regular transaction form.'
        });
        
        // Close the modal and let the user fall back to regular editing
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error loading transfer:', error);
      toast.error('Failed to load transfer data');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const getAccountName = (accountId: string) => {
    return accounts?.find(a => a.id === accountId)?.name || 'Unknown Account';
  };

  const getAccountCurrency = (accountId: string) => {
    return accounts?.find(a => a.id === accountId)?.currency || 'USD';
  };

  // Handle amount change and auto-calculate exchange rate if convertedAmount exists
  const handleAmountChange = (value: string) => {
    const amount = parseFloat(value) || 0;
    const convertedAmount = parseFloat(formData.convertedAmount) || 0;
    
    setFormData(prev => {
      const newData = { ...prev, amount: value };
      
      // Auto-calculate exchange rate if both amounts are valid
      if (amount > 0 && convertedAmount > 0) {
        const newRate = (convertedAmount / amount).toFixed(6);
        newData.exchangeRate = newRate;
      }
      
      return newData;
    });
  };

  // Handle converted amount change and auto-calculate exchange rate
  const handleConvertedAmountChange = (value: string) => {
    const convertedAmount = parseFloat(value) || 0;
    const amount = parseFloat(formData.amount) || 0;
    
    setFormData(prev => {
      const newData = { ...prev, convertedAmount: value };
      
      // Auto-calculate exchange rate if both amounts are valid
      if (amount > 0 && convertedAmount > 0) {
        const newRate = (convertedAmount / amount).toFixed(6);
        newData.exchangeRate = newRate;
      }
      
      return newData;
    });
  };

  // Handle exchange rate change and auto-calculate converted amount
  const handleExchangeRateChange = (value: string) => {
    const rate = parseFloat(value) || 0;
    const amount = parseFloat(formData.amount) || 0;
    
    setFormData(prev => {
      const newData = { ...prev, exchangeRate: value };
      
      // Auto-calculate converted amount if rate and amount are valid
      if (amount > 0 && rate > 0) {
        const newConvertedAmount = (amount * rate).toFixed(2);
        newData.convertedAmount = newConvertedAmount;
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!transfer || !user?.id || !actualTransferId) return;
    
    setIsSaving(true);
    try {
      const updates: UpdateTransferRequest = {
        amount: parseFloat(formData.amount),
        exchangeRate: parseFloat(formData.exchangeRate),
        description: formData.description,
        notes: formData.notes,
        date: new Date(formData.date).toISOString(),
        tags: formData.tags
      };
      
      await updateTransfer(user.id, actualTransferId, updates);
      
      toast.success('Transfer updated successfully', {
        description: 'All changes have been saved.'
      });
      
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Error updating transfer:', error);
      toast.error('Failed to update transfer', {
        description: 'Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTransfer = async () => {
    if (!transfer || !user?.id || !actualTransferId) return;

    // Confirm deletion
    if (!confirm('Are you sure you want to delete this transfer? Both transactions will be permanently removed. This action cannot be undone.')) {
      return;
    }

    try {
      await deleteTransfer(user.id, actualTransferId);
      
      toast.success('Transfer deleted successfully', {
        description: 'Both transactions have been removed.'
      });
      
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Error deleting transfer:', error);
      toast.error('Failed to delete transfer', {
        description: 'Please try again.'
      });
    }
  };

  const handleSplitTransfer = async () => {
    if (!transfer || !user?.id || !actualTransferId) return;

    // Confirm split
    if (!confirm('This will convert the transfer into separate income and expense transactions. This action cannot be undone.')) {
      return;
    }

    try {
      await splitTransfer(user.id, actualTransferId);
      
      toast.success('Transfer split successfully', {
        description: 'The transfer has been converted to separate income and expense transactions.'
      });
      
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Error splitting transfer:', error);
      toast.error('Failed to split transfer', {
        description: 'Please try again.'
      });
    }
  };

  if (!transfer) {
    return (
      <StyledModal
        isOpen={isOpen}
        onClose={onClose}
        title="Edit Transfer"
        maxWidth="500px"
      >
        <div className="text-center py-8">
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#439476] border-t-transparent mx-auto mb-4"></div>
              <p className="text-[#2F2F2F] dark:text-[#A3A3A3]">Loading transfer...</p>
            </>
          ) : (
            <p className="text-[#2F2F2F] dark:text-[#A3A3A3]">Transfer not found</p>
          )}
        </div>
      </StyledModal>
    );
  }

  const isDifferentCurrency = transfer.fromCurrency !== transfer.toCurrency;

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Transfer"
      maxWidth="700px"
      footer={
        <>
          <div className="flex justify-start gap-3 mr-auto">
            <StyledModalButton
              onClick={handleDeleteTransfer}
              variant="danger"
              icon={<Trash2 className="w-4 h-4" />}
            >
              Delete Transfer
            </StyledModalButton>
            
            <StyledModalButton
              onClick={handleSplitTransfer}
              variant="outline"
              icon={<Split className="w-4 h-4" />}
            >
              Split Transfer
            </StyledModalButton>
          </div>

          <div className="flex gap-3">
            <StyledModalButton
              onClick={onClose}
              variant="outline"
              disabled={isSaving}
            >
              Cancel
            </StyledModalButton>
            <StyledModalButton
              onClick={() => {
                const form = document.querySelector('form');
                if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
              }}
              variant="primary"
              disabled={isSaving || !formData.amount || !formData.exchangeRate}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </StyledModalButton>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
          Edit the transfer details including amount, exchange rate, and timing.
        </p>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Transfer Overview */}
              <div className="bg-background-primary/50 dark:bg-background-dark-primary/50 backdrop-blur-lg backdrop-saturate-150 rounded-xl p-4 border border-border dark:border-border-dark">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">{getAccountName(transfer.fromAccountId)}</div>
                    <div className="text-text-secondary dark:text-text-dark-secondary">{getAccountCurrency(transfer.fromAccountId)}</div>
                  </div>
                  <ArrowRightIcon className="h-5 w-5 text-secondary dark:text-primary-dark" />
                  <div className="text-sm text-right">
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">{getAccountName(transfer.toAccountId)}</div>
                    <div className="text-text-secondary dark:text-text-dark-secondary">{getAccountCurrency(transfer.toAccountId)}</div>
                  </div>
                </div>
              </div>

              {/* Amount Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Source Amount */}
                <div className="space-y-2">
                  <Label htmlFor="amount" className="text-text-primary dark:text-text-dark-primary">
                    Amount Out ({transfer.fromCurrency})
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="h-11 w-full rounded-xl border border-border bg-background-input dark:border-border-dark dark:bg-background-dark-input text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark"
                    placeholder="0.00"
                    required
                  />
                </div>

                {/* Destination Amount */}
                <div className="space-y-2">
                  <Label htmlFor="convertedAmount" className="text-text-primary dark:text-text-dark-primary">
                    Amount In ({transfer.toCurrency})
                  </Label>
                  <Input
                    id="convertedAmount"
                    type="number"
                    step="0.01"
                    value={formData.convertedAmount}
                    onChange={(e) => handleConvertedAmountChange(e.target.value)}
                    className="h-11 w-full rounded-xl border border-border bg-background-input dark:border-border-dark dark:bg-background-dark-input text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              {/* Exchange Rate */}
              {isDifferentCurrency && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                            <Label htmlFor="exchangeRate" className="text-text-primary dark:text-text-dark-primary">
          Exchange Rate ({(() => {
            const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
            const fromUnit = preciousMetals.includes(transfer.fromCurrency) ? `${transfer.fromCurrency}/g` : transfer.fromCurrency;
            const toUnit = preciousMetals.includes(transfer.toCurrency) ? `${transfer.toCurrency}/g` : transfer.toCurrency;
            return `${fromUnit} → ${toUnit}`;
          })()})
        </Label>
                    <button
                      type="button"
                      className="text-secondary dark:text-primary-dark hover:text-secondary/80 dark:hover:text-primary-dark/80 transition-colors"
                    >
                      <RefreshCwIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <Input
                    id="exchangeRate"
                    type="number"
                    step="0.000001"
                    value={formData.exchangeRate}
                    onChange={(e) => handleExchangeRateChange(e.target.value)}
                    className="h-11 w-full rounded-xl border border-border bg-background-input dark:border-border-dark dark:bg-background-dark-input text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark"
                    placeholder="1.000000"
                    required
                  />
                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    1 {(() => {
                      const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
                      return preciousMetals.includes(transfer.fromCurrency) ? `${transfer.fromCurrency}/g` : transfer.fromCurrency;
                    })()} = {formData.exchangeRate} {(() => {
                      const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
                      return preciousMetals.includes(transfer.toCurrency) ? `${transfer.toCurrency}/g` : transfer.toCurrency;
                    })()}
                  </div>
                </div>
              )}

              {/* Date Field */}
              <div className="space-y-2">
                <Label htmlFor="date" className="text-text-primary dark:text-text-dark-primary">Date & Time</Label>
                <div className="relative">
                  <Input
                    id="date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="h-11 w-full rounded-xl border border-border bg-background-input dark:border-border-dark dark:bg-background-dark-input text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark"
                    required
                  />
                  <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary dark:text-text-dark-secondary pointer-events-none" />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-text-primary dark:text-text-dark-primary">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="h-11 w-full rounded-xl border border-border bg-background-input dark:border-border-dark dark:bg-background-dark-input text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark"
                  placeholder="Description (optional)"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-text-primary dark:text-text-dark-primary">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full rounded-xl border border-border bg-background-input dark:border-border-dark dark:bg-background-dark-input text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark resize-none"
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
            </form>
        </div>
      </StyledModal>
    );
} 